import React, { useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import { fetchProperty, createBooking } from "@/lib/api";
import { useAuth } from "@/contexts/AuthProvider";
import BrandHeading from "@/components/brand/BrandHeading";
import BrandLabel from "@/components/brand/BrandLabel";
import BrandButton from "@/components/brand/BrandButton";
import BrandCard from "@/components/brand/BrandCard";
import BrandInput, { BrandField, BrandTextarea } from "@/components/brand/BrandInput";
import DateRangePicker from "@/components/DateRangePicker";
import { Checkbox } from "@/components/ui/checkbox";
import { Separator } from "@/components/ui/separator";
import { ChevronLeft, Check } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

const STEPS = ["Dates & guests", "Room", "Guest details", "Confirmation"];

// Per-night extras multiply by length-of-stay; one-time extras stay flat.
// `per_night` is the single source of truth for pricing; the label is purely
// presentational.  The flag is also persisted server-side on the booking so the
// breakdown can be reconstructed on /bookings, the detail view, and emails.
const EXTRAS = [
  { id: "breakfast", name: "Daily breakfast",          price: 28,  per_night: true  },
  { id: "spa",       name: "Daily spa & sauna access", price: 45,  per_night: true  },
  { id: "parking",   name: "Daily parking",            price: 18,  per_night: true  },
  { id: "transfer",  name: "Private airport transfer", price: 180, per_night: false },
  { id: "champagne", name: "Champagne on arrival",     price: 65,  per_night: false },
  { id: "late",      name: "Late check-out",           price: 35,  per_night: false },
];

// Strip basic HTML entities for display-only fallback (used in plain-text contexts).
const decode = (s) => (s || "").replace(/&amp;/g, "&");

const BookingFlow = ({ brand = "castlewood" }) => {
  const { id } = useParams();
  const [search] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [property, setProperty] = useState(null);
  const [step, setStep] = useState(0);
  const [check_in, setCheckIn] = useState("");
  const [check_out, setCheckOut] = useState("");
  const [guests, setGuests] = useState(2);
  const [roomId, setRoomId] = useState(search.get("room") || "");
  const [extras, setExtras] = useState([]);
  const [details, setDetails] = useState({ name: user?.name || "", email: user?.email || "", phone: "", requests: "" });
  const [bookingResult, setBookingResult] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => { fetchProperty(id).then((p) => { setProperty(p); if (!roomId) setRoomId(p.rooms?.[0]?.id || ""); }); }, [id]);
  useEffect(() => { if (user) setDetails((d) => ({ ...d, name: d.name || user.name, email: d.email || user.email })); }, [user]);

  const room = useMemo(() => property?.rooms?.find((r) => r.id === roomId) || null, [property, roomId]);
  const nights = useMemo(() => {
    if (!check_in || !check_out) return 0;
    const a = new Date(check_in); const b = new Date(check_out);
    return Math.max(0, Math.round((b - a) / (1000 * 60 * 60 * 24)));
  }, [check_in, check_out]);

  // Effective nights to use for line-item math.  We use max(1, nights) so the
  // summary still reads sensibly before the guest picks both endpoints.
  const billedNights = Math.max(1, nights || 1);

  // Per-extra computed cost (price for one-time, price\u00d7nights for per-night).
  const extraLineCost = (e) => (e.per_night ? (e.price || 0) * billedNights : (e.price || 0));
  const extrasTotal = useMemo(
    () => extras.reduce((a, e) => a + extraLineCost(e), 0),
    [extras, billedNights]
  );

  const subtotal = (room?.price_per_night || property?.price_from || 0) * billedNights;
  const total = subtotal + extrasTotal;

  const toggleExtra = (item) => setExtras((x) => x.find((e) => e.id === item.id) ? x.filter((e) => e.id !== item.id) : [...x, item]);

  const canAdvance = () => {
    if (step === 0) return !!(check_in && check_out && nights > 0 && guests > 0);
    if (step === 1) return !!room;
    if (step === 2) return !!(details.name && details.email);
    return true;
  };

  const submit = async () => {
    if (!property || !room) return;
    setBusy(true);
    try {
      const res = await createBooking({
        property_id: property.id,
        room_id: room.id,
        check_in, check_out, guests,
        guest_name: details.name,
        guest_email: details.email,
        guest_phone: details.phone,
        special_requests: details.requests,
        // Forward the per_night flag so the server can compute and persist the
        // correct breakdown (nightly extras \u00d7 nights vs flat).
        extras: extras.map((e) => ({ id: e.id, name: e.name, price: e.price, per_night: !!e.per_night })),
      });
      setBookingResult(res);
      setStep(3);
      toast.success("Booking confirmed");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Booking failed");
    } finally { setBusy(false); }
  };

  if (!property) return <div className="container-rail py-16">Loading…</div>;

  return (
    <div className="pb-32 md:pb-0" data-testid={`${brand}-booking-flow`}>
      <div className="container-rail py-8 md:py-12">
        <button onClick={() => navigate(-1)} className="text-[12px] text-muted-foreground hover:text-foreground inline-flex items-center gap-1 mb-6" data-testid="back"><ChevronLeft size={14} /> Back to {property.name}</button>
        <BrandLabel withRule>{brand === "vela" ? "Vela Reservation" : "Castlewood reservation"}</BrandLabel>
        <BrandHeading level={1} className="mt-3">Reserve your stay.</BrandHeading>
      </div>
      {/* Step indicator */}
      <div className="container-rail" data-testid="booking-steps">
        <div className="no-scrollbar overflow-x-auto scroll-fade-mask">
          <div className="flex items-center gap-4 min-w-max">
            {STEPS.map((s, i) => (
              <div key={s} className="flex items-center gap-2" data-testid={`step-${i}`}>
                <span className={cn("inline-flex items-center justify-center w-7 h-7 rounded-full border text-[12px] font-medium", i <= step ? "bg-foreground text-background border-foreground" : "border-border text-muted-foreground")}>{i + 1}</span>
                <span className={cn("text-[12px] uppercase tracking-[0.16em]", i === step ? "text-foreground" : "text-muted-foreground")}>{s}</span>
                {i < STEPS.length - 1 && <span className="w-12 h-px bg-border" />}
              </div>
            ))}
          </div>
        </div>
      </div>
      <div className="container-rail mt-8 grid lg:grid-cols-[1.6fr_1fr] gap-10 items-start">
        <div className="space-y-10">
          {step === 0 && (
            <BrandCard className="p-6 md:p-8">
              <div className="grid sm:grid-cols-[2fr_1fr] gap-4 items-end">
                <BrandField label="Arrive — Depart">
                  <DateRangePicker
                    mode="range"
                    value={{ from: check_in, to: check_out }}
                    onChange={(v) => { setCheckIn(v.from || ""); setCheckOut(v.to || ""); }}
                    placeholder="Choose your dates"
                    testId="book-date-range"
                  />
                </BrandField>
                <BrandField label="Guests">
                  <BrandInput data-testid="book-guests" type="number" min={1} max={20} value={guests} onChange={(e) => setGuests(Number(e.target.value || 1))} />
                </BrandField>
              </div>
              {check_in && check_out && nights <= 0 && <p className="text-[12px] text-destructive mt-3">Check-out must be after check-in.</p>}
              {nights > 0 && (
                <p className="mt-3 text-[12px] text-muted-foreground" data-testid="book-nights-readout">
                  {nights} night{nights !== 1 && "s"}
                </p>
              )}
            </BrandCard>
          )}
          {step === 1 && (
            <div className="space-y-4">
              {property.rooms?.map((r) => (
                <BrandCard key={r.id} onClick={() => setRoomId(r.id)} className={cn("cursor-pointer p-4 md:p-5 grid md:grid-cols-[200px_1fr_auto] gap-5 transition-colors", r.id === roomId && "border-foreground")} data-testid={`room-option-${r.id}`}>
                  <img src={r.image} alt={r.name} className="w-full h-32 md:h-full object-cover" />
                  <div>
                    <div className="font-display text-[22px] font-[700]">{r.name}</div>
                    <p className="mt-2 text-[13px] text-muted-foreground">{r.description}</p>
                    <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-[11px] uppercase tracking-[0.16em] text-muted-foreground">{r.amenities?.map((a) => <span key={a}>{a}</span>)} · <span>{r.size_sqm} m²</span> · <span>sleeps {r.max_guests}</span></div>
                  </div>
                  <div className="text-right md:flex md:flex-col md:items-end md:justify-between">
                    <div className="font-display text-[24px]">€{r.price_per_night}<span className="text-[12px] text-muted-foreground"> / n</span></div>
                    {r.id === roomId && <span className="inline-flex items-center text-[11px] uppercase tracking-[0.16em] text-foreground mt-3"><Check size={12} className="mr-1" /> Selected</span>}
                  </div>
                </BrandCard>
              ))}
            </div>
          )}
          {step === 2 && (
            <BrandCard className="p-6 md:p-8 space-y-5">
              <div className="grid sm:grid-cols-2 gap-4">
                <BrandField label="Full name" required><BrandInput data-testid="book-name" value={details.name} onChange={(e) => setDetails({ ...details, name: e.target.value })} /></BrandField>
                <BrandField label="Email" required><BrandInput data-testid="book-email" type="email" value={details.email} onChange={(e) => setDetails({ ...details, email: e.target.value })} /></BrandField>
                <BrandField label="Phone"><BrandInput data-testid="book-phone" value={details.phone} onChange={(e) => setDetails({ ...details, phone: e.target.value })} /></BrandField>
              </div>
              <BrandField label="Special requests"><BrandTextarea data-testid="book-requests" rows={4} placeholder="Anything we should know?" value={details.requests} onChange={(e) => setDetails({ ...details, requests: e.target.value })} /></BrandField>
              <div>
                <BrandLabel>Add to your stay</BrandLabel>
                <div className="mt-3 grid sm:grid-cols-2 gap-3">
                  {EXTRAS.map((it) => {
                    const on = extras.find((e) => e.id === it.id);
                    return (
                      <label key={it.id} className={cn("flex items-center justify-between gap-3 border border-border p-3 cursor-pointer transition-colors", on && "border-foreground bg-secondary")} data-testid={`extra-${it.id}`}>
                        <span className="flex items-start gap-3">
                          <Checkbox checked={!!on} onCheckedChange={() => toggleExtra(it)} className="mt-0.5" />
                          <span className="text-[14px] leading-tight">
                            {it.name}
                            <span className="block text-[11px] uppercase tracking-[0.14em] text-muted-foreground mt-0.5">
                              {it.per_night ? "per night" : "one-time"}
                            </span>
                          </span>
                        </span>
                        <span className="text-[13px] font-medium text-right whitespace-nowrap">
                          €{it.price}
                          {it.per_night && (
                            <span className="block text-[11px] font-normal text-muted-foreground">/ night</span>
                          )}
                        </span>
                      </label>
                    );
                  })}
                </div>
              </div>
            </BrandCard>
          )}
          {step === 3 && bookingResult && (
            <BrandCard className="p-8 md:p-10" data-testid="booking-confirmation">
              <BrandLabel withRule>Confirmed</BrandLabel>
              <BrandHeading level={1} className="mt-4">Thank you, {bookingResult.guest_name.split(" ")[0]}.</BrandHeading>
              <p className="mt-3 max-w-[58ch] text-muted-foreground">Your reservation at <span className="font-display text-foreground">{bookingResult.property_name}</span> has been received. A confirmation has been noted; the hotel will be in touch with arrival details.</p>
              <Separator className="my-8" />
              <div className="grid sm:grid-cols-2 gap-6 text-[14px]">
                <div><BrandLabel>Reference</BrandLabel><div className="mt-2 font-mono text-[13px]">{bookingResult.id}</div></div>
                <div><BrandLabel>Room</BrandLabel><div className="mt-2">{bookingResult.room_name}</div></div>
                <div><BrandLabel>Dates</BrandLabel><div className="mt-2">{bookingResult.check_in} → {bookingResult.check_out} · {bookingResult.nights} night{bookingResult.nights !== 1 && "s"}</div></div>
                <div><BrandLabel>Total</BrandLabel><div className="mt-2 font-display text-[22px]">€{Math.round(bookingResult.total)}</div></div>
              </div>

              {/* Itemised breakdown so the guest can see exactly how the total was computed. */}
              <Separator className="my-8" />
              <BrandLabel>Price breakdown</BrandLabel>
              <div className="mt-3 space-y-2 text-[13px]" data-testid="confirmation-breakdown">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">{bookingResult.room_name} · €{Math.round((bookingResult.total - (bookingResult.extras_total || 0)) / Math.max(1, bookingResult.nights))} × {bookingResult.nights} night{bookingResult.nights !== 1 && "s"}</span>
                  <span>€{Math.round((bookingResult.total - (bookingResult.extras_total || 0)))}</span>
                </div>
                {(bookingResult.extras || []).map((e) => (
                  <div key={e.id} className="flex justify-between">
                    <span className="text-muted-foreground">
                      {e.name}
                      {e.per_night ? ` · €${e.price} × ${bookingResult.nights} night${bookingResult.nights !== 1 ? "s" : ""}` : ""}
                    </span>
                    <span>€{e.per_night ? (e.price * bookingResult.nights) : e.price}</span>
                  </div>
                ))}
                <Separator className="my-2" />
                <div className="flex justify-between font-display text-[16px]"><span>Total</span><span>€{Math.round(bookingResult.total)}</span></div>
              </div>

              <div className="mt-8 flex flex-wrap gap-3">
                <BrandButton to="/bookings" data-testid="view-bookings">View your stays</BrandButton>
                <BrandButton to={`/${brand}`} variant="secondary" data-testid="back-home">Back to {brand}</BrandButton>
              </div>
            </BrandCard>
          )}
          {step < 3 && (
            <div className="flex items-center justify-between">
              <button onClick={() => setStep((s) => Math.max(0, s - 1))} className="text-[12px] uppercase tracking-[0.18em] text-muted-foreground hover:text-foreground" disabled={step === 0} data-testid="step-prev">Back</button>
              {step < 2 ? (
                <BrandButton disabled={!canAdvance()} onClick={() => setStep((s) => s + 1)} data-testid="step-next">Continue</BrandButton>
              ) : (
                <BrandButton disabled={!canAdvance() || busy} onClick={submit} data-testid="step-confirm">{busy ? "Reserving…" : "Confirm reservation"}</BrandButton>
              )}
            </div>
          )}
        </div>
        {/* Summary */}
        <aside className="lg:sticky lg:top-24 self-start">
          <BrandCard className="p-6">
            <BrandLabel>Your stay</BrandLabel>
            <img src={property.hero_image} alt={property.name} className="mt-4 w-full aspect-[4/3] object-cover" />
            <div className="mt-4 font-display text-[22px] font-[700]">{property.name}</div>
            <div className="text-[12px] text-muted-foreground">{property.location}</div>
            <Separator className="my-4" />
            <div className="grid grid-cols-2 gap-3 text-[13px]">
              <div><div className="text-[11px] text-muted-foreground uppercase tracking-[0.16em]">In</div><div>{check_in || "—"}</div></div>
              <div><div className="text-[11px] text-muted-foreground uppercase tracking-[0.16em]">Out</div><div>{check_out || "—"}</div></div>
              <div><div className="text-[11px] text-muted-foreground uppercase tracking-[0.16em]">Guests</div><div>{guests}</div></div>
              <div><div className="text-[11px] text-muted-foreground uppercase tracking-[0.16em]">Room</div><div className="truncate">{room?.name || "—"}</div></div>
            </div>
            <Separator className="my-4" />
            <div className="space-y-2 text-[13px]" data-testid="summary-breakdown">
              <div className="flex justify-between">
                <span className="text-muted-foreground">
                  Room · €{room?.price_per_night || property?.price_from || 0} × {billedNights} night{billedNights !== 1 && "s"}
                </span>
                <span>€{Math.round(subtotal)}</span>
              </div>
              {extras.map((e) => (
                <div key={e.id} className="flex justify-between" data-testid={`summary-extra-${e.id}`}>
                  <span className="text-muted-foreground">
                    {e.name}
                    {e.per_night && (
                      <span> · €{e.price} × {billedNights} night{billedNights !== 1 && "s"}</span>
                    )}
                  </span>
                  <span>€{extraLineCost(e)}</span>
                </div>
              ))}
              <Separator className="my-2" />
              <div className="flex justify-between font-display text-[18px]"><span>Total</span><span>€{Math.round(total)}</span></div>
            </div>
          </BrandCard>
        </aside>
      </div>

      {step < 3 && (
        <div className="fixed bottom-0 inset-x-0 z-30 md:hidden bg-background border-t border-border p-3 flex items-center justify-between gap-3" data-testid="sticky-step">
          <div>
            <div className="text-[11px] text-muted-foreground">Total</div>
            <div className="font-display text-[18px]">€{Math.round(total)}</div>
          </div>
          {step < 2 ? (
            <BrandButton disabled={!canAdvance()} onClick={() => setStep((s) => s + 1)} data-testid="sticky-next">Continue</BrandButton>
          ) : (
            <BrandButton disabled={!canAdvance() || busy} onClick={submit} data-testid="sticky-confirm">{busy ? "…" : "Confirm"}</BrandButton>
          )}
        </div>
      )}
    </div>
  );
};
export default BookingFlow;
export { BookingFlow };
